import { useMemo } from 'react';

import { useGetCustomerTypesEnabledForLspQuery } from 'src/app/queries/customers/get-customer-types-enabled-for-lsp';
import { useGetDiscountGroupsQuery } from 'src/app/queries/discounts/get-discount-groups';
import { useLspLocationsQuery } from 'src/app/queries/posv3/maintenance/get-lsp-locations-backend';
import { useGetSegmentsLiteQuery } from 'src/app/queries/segment/get-segments-lite';
import { useOnlinePlatformTypesQuery } from 'src/app/queries/v2/preorder/get-platform-menu-types';

import type { DropdownMenuOption } from 'src/app/components/lib/dropdown';

export function useDetailsStepData() {
  const { data: customerTypesData, isLoading: customerTypesDataLoading } = useGetCustomerTypesEnabledForLspQuery();
  const { data: discountGroupsData, isLoading: discountGroupsDataLoading } = useGetDiscountGroupsQuery();
  const { data: locationsData, isLoading: locationsDataLoading } = useLspLocationsQuery();
  const { data: segmentsData, isLoading: segmentsDataLoading } = useGetSegmentsLiteQuery();
  const { data: onlinePlatformTypesData, isLoading: onlinePlatformTypesLoading } = useOnlinePlatformTypesQuery();

  const customerTypesOptions: DropdownMenuOption[] = useMemo(
    () =>
      Array.isArray(customerTypesData)
        ? customerTypesData
            .map((type) => ({
              id: type.CustomerTypeId,
              value: type.CustomerTypeId,
              label: type.CustomerType,
            }))
            .sort((a, b) => a.label?.localeCompare(b.label))
        : [],
    [customerTypesData]
  );

  const discountGroupOptions: DropdownMenuOption[] = useMemo(() => {
    if (!Array.isArray(discountGroupsData)) {
      return [];
    }
    const activeDiscountGroups = discountGroupsData.filter((group) => group.Active && !group.Expired);
    return activeDiscountGroups.map((group) => ({
      id: group.Id,
      value: group.Id,
      label: group.GroupName,
    }));
  }, [discountGroupsData]);

  const locationOptions: DropdownMenuOption[] = useMemo(
    () =>
      Array.isArray(locationsData)
        ? locationsData.map((location) => ({
            id: location.LocId,
            value: location.LocId,
            label: location.Name,
          }))
        : [],
    [locationsData]
  );

  const segmentOptions: DropdownMenuOption[] = useMemo(() => {
    if (!Array.isArray(segmentsData)) {
      return [];
    }
    const activeSegments = segmentsData.filter((segment) => !segment.IsDeleted);
    return activeSegments.map((segment) => ({
      id: segment.SegmentId,
      value: segment.SegmentId,
      label: segment.Name,
    }));
  }, [segmentsData]);

  const orderSourceOptions: DropdownMenuOption[] = useMemo(
    () =>
      Array.isArray(onlinePlatformTypesData)
        ? onlinePlatformTypesData.map((t) => ({
            id: t.Id,
            value: t.Id,
            label: t.Description,
          }))
        : [],
    [onlinePlatformTypesData]
  );

  return {
    customerTypesDataLoading,
    customerTypesOptions,
    discountGroupOptions,
    discountGroupsDataLoading,
    onlinePlatformTypesLoading,
    locationOptions,
    locationsDataLoading,
    orderSourceOptions,
    segmentOptions,
    segmentsDataLoading,
  };
}
